import { Platform } from 'react-native';
import { getTheme, type AppTheme } from './index';
import contract from './appearance-contract.json';
import { bundledBaseline } from './bundledPresentation';
import { validateAppearance } from './appearance-validation';

export { validateAppearance };
export type AppearanceScope = 'all' | 'ios' | 'android' | 'web' | 'desktop';
export type AppearanceMode = 'light' | 'dark';
export type TokenValues = Record<string, string | number>;
export type AppearanceConfig = { version: 1 } & Partial<Record<AppearanceScope, Partial<Record<AppearanceMode, TokenValues>>>>;
export type AppearanceField = { label: string; group: string; type: string; min?: number; max?: number; options?: string[] };
export const appearanceFields = contract.fields as Record<string, AppearanceField>;
export const appearanceScopes: AppearanceScope[] = ['all', 'ios', 'android', 'web', 'desktop'];
export const emptyAppearance = (): AppearanceConfig => ({ version: 1 });
export const isAppearance = (value: unknown): value is AppearanceConfig | null => validateAppearance(value).errors.length === 0;
/**
 * Stored appearance can name controls that were later removed (the plan-card
 * tokens). Readers drop those keys and keep the rest; writes stay strict, so a
 * new proposal can never name a control that does not exist.
 */
export function dropRetiredControls<T>(value: T): T {
  const config = value as any;
  if (!config || typeof config !== 'object' || Array.isArray(config)) return value;
  const next: any = {};
  for (const [scope, modes] of Object.entries(config)) {
    if (!appearanceScopes.includes(scope as AppearanceScope) || !modes || typeof modes !== 'object') { next[scope] = modes; continue; }
    next[scope] = {};
    for (const [mode, tokens] of Object.entries(modes as Record<string, any>)) {
      if (!tokens || typeof tokens !== 'object' || Array.isArray(tokens)) { next[scope][mode] = tokens; continue; }
      next[scope][mode] = Object.fromEntries(Object.entries(tokens).filter(([path]) => Object.prototype.hasOwnProperty.call(appearanceFields, path)));
    }
  }
  return next;
}
export const tokenValue = (theme: AppTheme, path: string): string | number => path.split('.').reduce((value: any, key) => value[key], theme);

export function resolveAppearance(base: AppTheme, stored: AppearanceConfig | null, scope: AppearanceScope): AppTheme {
  const config = dropRetiredControls(stored);
  if (!config || !isAppearance(config)) return base;
  const mode = base.dark ? 'dark' : 'light';
  const tokens = { ...config.all?.[mode], ...(scope === 'all' ? {} : config[scope]?.[mode]) };
  if (!Object.keys(tokens).length) return base;
  // Clone before applying allow-listed paths. The bundled defaults never mutate.
  const theme = JSON.parse(JSON.stringify(base));
  for (const [path, value] of Object.entries(tokens)) {
    const parts = path.split('.');
    const leaf = parts.pop()!;
    const parent = parts.reduce((obj, key) => obj[key], theme);
    parent[leaf] = value;
  }
  if (tokens['colors.primary'] && !tokens['colors.primarySubtle']) theme.colors.primarySubtle = `${theme.colors.primary}15`;
  if (tokens['colors.primary'] && !tokens['colors.primaryStrong']) theme.colors.primaryStrong = theme.colors.primary;
  if (tokens['colors.muted'] && !tokens['colors.textMuted']) theme.colors.textMuted = theme.colors.muted;
  return theme as AppTheme;
}

export function resolveBundledAppearance(base: AppTheme, scope: AppearanceScope): AppTheme {
  return [...(bundledBaseline.appearanceLayers || []), bundledBaseline.appearance].reduce((theme, layer) => resolveAppearance(theme, layer, scope), base);
}

export function previewAppearance(config: AppearanceConfig | null, scope: AppearanceScope, mode: AppearanceMode) {
  const defaults = resolveBundledAppearance(getTheme(mode), scope);
  return resolveAppearance(defaults, config, scope);
}

export function setAppearanceToken(config: AppearanceConfig, scope: AppearanceScope, mode: AppearanceMode, path: string, value?: string | number): AppearanceConfig {
  const next = JSON.parse(JSON.stringify(config)) as AppearanceConfig;
  const tokens = { ...next[scope]?.[mode] };
  if (value === undefined) delete tokens[path]; else tokens[path] = value;
  next[scope] = { ...next[scope], [mode]: tokens };
  if (!Object.keys(tokens).length) delete next[scope]![mode];
  if (!Object.keys(next[scope]!).length) delete next[scope];
  return next;
}

export function appearanceFontFamily(family: string, platform = Platform.OS): string | undefined {
  if (family === 'serif') return platform === 'android' ? 'serif' : 'Georgia';
  if (family === 'monospace') return platform === 'ios' ? 'Menlo' : 'monospace';
  return undefined; // Keep each operating system's current system font.
}
